// helpers/vault/addMediaToCategory.helpers.ts

export type VaultCategoryLite = { id: string; name: string }

// Generic: merge items to the TOP, no duplicates by `id`
export const mergeUniqueTop = <T extends { id?: number | string }>(
  base: T[],
  items: T[]
): T[] => {
  const next = Array.isArray(base) ? [...base] : []
  const seen = new Set(next.map(x => x?.id))

  for (const it of items) {
    if (it?.id == null || seen.has(it.id)) continue
    next.unshift(it)
    seen.add(it.id)
  }

  return next
}

// Vault-specific: ensure a vault POST entry has the selected category in post.media_categories
export const applyCategoryToPostEntry = (
  entry: any,
  category: VaultCategoryLite
) => {
  if (!entry?.post) return entry

  const catId = Number(category.id)
  if (!catId) return entry

  const post = entry.post
  const cats = Array.isArray(post.media_categories) ? post.media_categories : []
  const has = cats.some((c: any) => Number(c?.id) === catId)
  if (has) return entry

  return {
    ...entry,
    post: {
      ...post,
      media_categories: [
        ...cats,
        {
          id: catId,
          name: String(category.name ?? ''),
          public: 1,
          premium: null,
          created_at: null,
          selected: null,
          suggestions: 1,
        },
      ],
    },
  }
}

// Convenience: take the selected vault entries and return patched POST entries only
export const buildOptimisticAddedPostEntries = (
  selectedVaultEntries: any[],
  category: VaultCategoryLite
): any[] => {
  const entries = Array.isArray(selectedVaultEntries) ? selectedVaultEntries : []
  return entries
    .filter(it => !!it?.post)
    .map(it => applyCategoryToPostEntry(it, category))
}

// Convenience: optimistic update that works for BOTH component state and zustand cache
export const optimisticAddPostsToVaultState = (args: {
  currentMedia: any[]
  currentCache: any | undefined
  vaultKey: string
  patchedPostEntries: any[]
}) => {
  const { currentMedia, currentCache, vaultKey, patchedPostEntries } = args

  const nextMedia = mergeUniqueTop(currentMedia, patchedPostEntries)

  const nextCache =
    currentCache?.data && Array.isArray(currentCache.data)
      ? { ...currentCache, data: mergeUniqueTop(currentCache.data, patchedPostEntries) }
      : { data: patchedPostEntries, nextPage: null, existingIds: [] }

  return { nextMedia, nextCache, vaultKey }
}